// An editable combobox that lives inside a modal (spec 054, research R12): the
// stock movement dialog's medicine search and its lot field. Generalised from
// CorrectableLotCombobox (apps/frontoffice/src/pages/operator/corrections), the
// first combobox that had to work inside `mud-modal`.
//
// The panel is NOT portalled to <body>. `mud-modal` opens a native <dialog>
// with showModal(), which renders in the browser's top layer, and anything
// under <body> paints beneath its backdrop whatever its z-index. Kept in this
// subtree the panel is in the top layer with the dialog, and
// `useAnchoredOverlay`'s `position: fixed` keeps the modal body's
// `overflow-y: auto` from clipping it.
//
// Keys the list consumes stop here. The modal closes on the native `cancel`
// that follows an unprevented Escape, and the movement dialog submits on Enter;
// an Escape or Enter meant for the list must reach neither.
//
// Two shapes (spec 054 T106). The lot field is a labelled `mud-text-input` whose
// list floats over the fields below it. The medicine search is MUD's search
// pattern (decision D4): a `mud-text-input` with a search glyph in `icon-start`
// and no label, and `inline`, its list in the dialog body under it, so the
// dialog holds it rather than a panel spilling past its edge. (It is not
// `mud-search-input`: that lost `invalid` and `readonly` in MUD 1.2.)
import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ElementRef,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { MudIcon, MudTextInput } from '@egov-moldova/mud-react';
import { useAnchoredOverlay } from './anchored-overlay';
import { useComboboxA11y } from './combobox-a11y';

// Both shapes are a `mud-text-input`, whose focusable `<input>` lives in its
// own shadow root, which is where `useComboboxA11y` writes the combobox role
// and state.
type FieldElement = ElementRef<typeof MudTextInput>;

export interface DialogComboboxGroup<T> {
  key: string;
  /**
   * The group's accessible name. With one, the options sit in a
   * `role="group"` (listbox > group > option, ARIA's grouped listbox); without
   * one they sit in the listbox directly.
   */
  label?: string | undefined;
  /** What the group shows above its options; hidden from assistive technology, which reads `label`. */
  heading?: ReactNode;
  options: readonly T[];
}

/** An option that is not a value, drawn first: "Lot nou" in the lot field. */
export interface DialogComboboxSpecialOption {
  key: string;
  content: ReactNode;
  onSelect: () => void;
}

export interface DialogComboboxProps<T> {
  value: string;
  onValueChange: (value: string) => void;
  groups: readonly DialogComboboxGroup<T>[];
  optionKey: (option: T) => string;
  renderOption: (option: T, state: { active: boolean }) => ReactNode;
  /**
   * An option's accessible name, in words. Without it the option is named by
   * its content, which a highlight splits (`<mark>AB12</mark>4` reads "AB12 4")
   * and a row laid out in columns runs together.
   */
  optionLabel?: ((option: T) => string) | undefined;
  onSelect: (option: T) => void;
  /** The listbox's accessible name. */
  listboxLabel: string;
  special?: DialogComboboxSpecialOption | undefined;
  /** A column-heading row between the special option and the groups; presentation only. */
  header?: ReactNode;
  /**
   * A note announced politely (loading, no results, a failed search), above the
   * list. It may hold an action, e.g. a failed search's "Reîncearcă" (T107).
   */
  status?: ReactNode;
  /** A note under the list, e.g. how many results were left out. */
  footer?: ReactNode;
  label?: string | undefined;
  placeholder?: string | undefined;
  helperText?: string | undefined;
  errorText?: string | undefined;
  ariaLabel?: string | undefined;
  invalid?: boolean | undefined;
  /**
   * Marks the field mandatory. Passed to `mud-text-input`, which draws AGE's red
   * asterisk after the label and makes the inner input `required`; the label
   * text carries no asterisk of its own.
   */
  required?: boolean | undefined;
  readonly?: boolean | undefined;
  disabled?: boolean | undefined;
  size?: 'md' | 'lg' | undefined;
  /**
   * `input` (the default) is a labelled `mud-text-input`; `search` is
   * a `mud-text-input` with a search glyph in `icon-start`, which carries no
   * label and is named by `ariaLabel`. Its clear button stays off: a clear
   * button binds Escape to clearing, and here Escape closes the list and keeps
   * the query.
   */
  field?: 'input' | 'search' | undefined;
  /** The query is running: the search field shows AGE's spinner and is `aria-busy`. */
  loading?: boolean | undefined;
  /**
   * The list in flow under the field, as a menu of the dialog body, instead of
   * a panel placed over what follows. Its height and scrolling are the
   * consumer's CSS (`.simsm-dialog-combobox__panel--inline`).
   */
  inline?: boolean | undefined;
  /** The field's host element, for a caller that moves focus to the field. */
  inputRef?: MutableRefObject<FieldElement | null> | undefined;
  className?: string | undefined;
}

function shown(node: ReactNode): boolean {
  return node !== undefined && node !== null && node !== false && node !== '';
}

/**
 * Whether `node` is `root` or inside it, crossing shadow boundaries upwards.
 * `mud-text-input`'s focusable input lives in its shadow root; a `relatedTarget` is
 * normally retargeted to the host, but walking through the shadow host keeps
 * the check right when it is not.
 */
function containsAcrossShadow(root: Node | null, node: EventTarget | null): boolean {
  if (root === null || !(node instanceof Node)) return false;
  let current: Node | null = node;
  while (current !== null) {
    if (current === root) return true;
    current = current instanceof ShadowRoot ? current.host : current.parentNode;
  }
  return false;
}

export function DialogCombobox<T>({
  value,
  onValueChange,
  groups,
  optionKey,
  renderOption,
  optionLabel,
  onSelect,
  listboxLabel,
  special,
  header,
  status,
  footer,
  label,
  placeholder,
  helperText,
  errorText,
  ariaLabel,
  invalid,
  required,
  readonly,
  disabled,
  size = 'md',
  field = 'input',
  loading,
  inline,
  inputRef,
  className,
}: DialogComboboxProps<T>): JSX.Element {
  const [open, setOpen] = useState(false);
  // Escape hides the panel without clearing what was typed; the next keystroke
  // or focus opens it again.
  const [dismissed, setDismissed] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<FieldElement | null>(null);
  const listboxRef = useRef<HTMLDivElement>(null);

  const options = useMemo(() => groups.flatMap((group) => group.options), [groups]);
  const offset = special ? 1 : 0;
  const optionCount = offset + options.length;
  const hasList = optionCount > 0 || shown(header);
  const hasContent = hasList || shown(status) || shown(footer);
  const locked = readonly === true || disabled === true;
  const panelOpen = open && !dismissed && !locked && hasContent;

  // An inline list is placed by the flow of the dialog body, not measured.
  const { style: panelStyle, placement } = useAnchoredOverlay({
    open: panelOpen && inline !== true,
    triggerRef: rootRef,
    overlayRef: panelRef,
    matchTriggerWidth: true,
  });

  // A pointer gesture that began inside: the pointerdown on an option can blur
  // the field before the click that picks it lands, and that blur is not the
  // user leaving. Cleared once the gesture is over.
  const pointerInsideRef = useRef(false);

  useEffect(() => {
    function onPointerDown(event: PointerEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) {
        pointerInsideRef.current = false;
        setOpen(false);
      }
    }
    function onGestureEnd(): void {
      pointerInsideRef.current = false;
    }
    document.addEventListener('pointerdown', onPointerDown);
    // Capture, so a click the panel's own handlers stop still ends the gesture.
    // Not pointerup: on touch the compatibility mousedown, and the focus move it
    // makes, come after it.
    document.addEventListener('click', onGestureEnd, true);
    document.addEventListener('pointercancel', onGestureEnd, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('click', onGestureEnd, true);
      document.removeEventListener('pointercancel', onGestureEnd, true);
    };
  }, []);

  // Focus leaving the combobox shuts the panel: the lot list must not stay open
  // over the quantity field once the user has moved on to it. Focus moving
  // between the field and the options stays inside `rootRef`, the panel being
  // rendered in this subtree.
  function onRootBlur(event: ReactFocusEvent): void {
    if (pointerInsideRef.current) return;
    if (containsAcrossShadow(rootRef.current, event.relatedTarget)) return;
    setOpen(false);
  }

  function reopen(): void {
    setOpen(true);
    setDismissed(false);
  }

  const combobox = useComboboxA11y({
    open: panelOpen,
    optionCount,
    fieldRef,
    listboxRef,
    onSelect: (index) => {
      setOpen(false);
      if (special && index === 0) {
        special.onSelect();
        return;
      }
      const option = options[index - offset];
      if (option !== undefined) onSelect(option);
    },
    onOpen: reopen,
    onClose: () => setDismissed(true),
    onTypeahead: (character) => {
      onValueChange(value + character);
      reopen();
    },
  });

  function onRootKeyDown(event: ReactKeyboardEvent): void {
    pointerInsideRef.current = false;
    const inList = listboxRef.current?.contains(event.target as Node) ?? false;
    // The search shape used to be MUD's search input, which prevents every Enter
    // itself (it fires `mudSearch`), so an Enter typed in the field never reached
    // the dialog's submit. A text input does not, so the same is done here, ahead
    // of the check below. Only a key this combobox consumed is kept from the host.
    if (field === 'search' && !inList && event.key === 'Enter') event.preventDefault();
    const preventedBefore = event.isDefaultPrevented();
    // Tab in the field leaves the combobox. Left to itself it would walk into the
    // listbox (it is in the tab order, next in the DOM); shutting the panel first
    // lets the browser's own Tab carry focus to the next field. Not consumed.
    // A panel with no list is left open: Tab reaches an action in its note (a
    // failed search's "Reîncearcă", spec 054 T107), and the blur shuts it once
    // focus leaves the combobox.
    if (!inList && event.key === 'Tab' && !event.altKey && !event.ctrlKey && !event.metaKey) {
      if (panelOpen && hasList) setOpen(false);
      return;
    }
    const modified = event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
    // Enter in the field takes the first result: typing and Enter is the
    // shortest path the wireframe draws (SC-003), and the dialog around this
    // would otherwise read the same Enter as "add the row".
    if (!inList && !modified && event.key === 'Enter' && panelOpen && optionCount > 0) {
      event.preventDefault();
      event.stopPropagation();
      combobox.selectOption(0);
      return;
    }
    combobox.onFieldKeyDown(event);
    if (!preventedBefore && event.isDefaultPrevented()) event.stopPropagation();
  }

  function onListKeyDown(event: ReactKeyboardEvent): void {
    combobox.onListboxKeyDown(event);
    if (event.isDefaultPrevented()) event.stopPropagation();
  }

  let index = -1;
  function renderOptionRow(
    key: string,
    content: ReactNode,
    isSpecial = false,
    name?: string,
  ): JSX.Element {
    index += 1;
    const at = index;
    return (
      <div
        key={key}
        id={combobox.optionId(at)}
        role="option"
        tabIndex={-1}
        aria-selected={combobox.activeIndex === at}
        {...(name === undefined ? {} : { 'aria-label': name })}
        className={
          isSpecial
            ? 'simsm-dialog-combobox__option simsm-dialog-combobox__option--special'
            : 'simsm-dialog-combobox__option'
        }
        onFocus={() => combobox.onOptionFocus(at)}
        onClick={() => combobox.selectOption(at)}
      >
        {content}
      </div>
    );
  }

  const showsError = errorText !== undefined && errorText !== '';

  const setField = (element: FieldElement | null): void => {
    fieldRef.current = element;
    if (inputRef) inputRef.current = element;
  };
  const onFieldInput = (event: CustomEvent<{ value: string }>): void => {
    if (locked) return;
    onValueChange(event.detail.value);
    reopen();
  };
  const onFieldFocus = (): void => {
    if (locked || !combobox.shouldOpenOnFieldFocus()) return;
    reopen();
  };
  const fieldTexts = {
    ...(label === undefined ? {} : { label }),
    ...(placeholder === undefined ? {} : { placeholder }),
    ...(helperText === undefined ? {} : { helperText }),
    ...(errorText === undefined ? {} : { errorText }),
    ...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel }),
  };

  return (
    <div
      ref={rootRef}
      className={className ? `simsm-dialog-combobox ${className}` : 'simsm-dialog-combobox'}
      onKeyDown={onRootKeyDown}
      onBlur={onRootBlur}
      onPointerDown={() => {
        pointerInsideRef.current = true;
      }}
    >
      {/* Both shapes are a MudTextInput. MUD 1.2's `mud-search-input` has no
          `invalid` or `readonly`, which this field needs, so the search shape
          is a text input with MUD's own search glyph in `icon-start`. */}
      <MudTextInput
        ref={setField}
        size={size}
        value={value}
        autocomplete="off"
        {...(field === 'search' ? { clearable: false, loading: loading === true } : {})}
        invalid={invalid === true || showsError}
        required={required === true}
        readonly={readonly === true}
        disabled={disabled === true}
        {...fieldTexts}
        onMudInput={onFieldInput}
        onMudFocus={onFieldFocus}
      >
        {field === 'search' ? (
          <MudIcon slot="icon-start" name="search" size={size === 'lg' ? 24 : 20} />
        ) : null}
      </MudTextInput>
      {panelOpen ? (
        <div
          ref={panelRef}
          // `--empty`: only a note to show (no results, a failed search), which
          // an inline list's CSS lets shrink to its content (spec 054 T107).
          className={[
            'simsm-dialog-combobox__panel',
            inline ? 'simsm-dialog-combobox__panel--inline' : '',
            hasList ? '' : 'simsm-dialog-combobox__panel--empty',
          ]
            .filter((name) => name !== '')
            .join(' ')}
          {...(inline ? {} : { style: panelStyle, 'data-placement': placement ?? undefined })}
        >
          {shown(status) ? (
            <div className="simsm-dialog-combobox__status" role="status">
              {status}
            </div>
          ) : null}
          {hasList ? (
            <div
              ref={listboxRef}
              id={combobox.listboxId}
              role="listbox"
              // In the tab order and named: the panel is a scroll container
              // whose only other contents carry tabindex="-1".
              tabIndex={0}
              aria-label={listboxLabel}
              className="simsm-dialog-combobox__list"
              onKeyDown={onListKeyDown}
            >
              {special ? renderOptionRow(special.key, special.content, true) : null}
              {shown(header) ? (
                <div
                  role="presentation"
                  aria-hidden="true"
                  className="simsm-dialog-combobox__header"
                >
                  {header}
                </div>
              ) : null}
              {groups.map((group) => {
                const rows = group.options.map((option) => {
                  const at = index + 1;
                  return renderOptionRow(
                    `${group.key}:${optionKey(option)}`,
                    renderOption(option, { active: combobox.activeIndex === at }),
                    false,
                    optionLabel?.(option),
                  );
                });
                if (group.label === undefined) return <Fragment key={group.key}>{rows}</Fragment>;
                return (
                  <div
                    key={group.key}
                    role="group"
                    aria-label={group.label}
                    className="simsm-dialog-combobox__group"
                  >
                    {shown(group.heading) ? (
                      <div className="simsm-dialog-combobox__group-heading" aria-hidden="true">
                        {group.heading}
                      </div>
                    ) : null}
                    {rows}
                  </div>
                );
              })}
            </div>
          ) : null}
          {shown(footer) ? <div className="simsm-dialog-combobox__footer">{footer}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
